import React from 'react';

import { Bolt, Crosshair, LayoutGrid, Sparkles, Sprout, TriangleAlert } from 'lucide-react';

import { IMSARMBTSReport } from '../../../../types/services.interfaces/mbts-ux';

import {
    SECTION_IDS,
    TViewMode,
    getFrictionLabel,
    getHeadline,
    getNarrativeBodyHtml,
    getOverallScore,
    getProficiencyCategory,
    getProficiencyTone,
    getQuickWinLabel,
    getSimulationDurationLabel,
    scrollToSectionId,
} from './utils';

interface ReportHeroProps {
    report: IMSARMBTSReport;
    viewMode: TViewMode;
    onViewModeChange: (mode: TViewMode) => void;
    /** Applied only to the hero section card — page title stays out of focus mode */
    spotlightClassName?: string;
    spotlightHandlers?: {
        onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
        onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
    };
};

export const ReportHero = (props: ReportHeroProps) => {
    const { report, viewMode, onViewModeChange, spotlightClassName, spotlightHandlers } = props;

    const score = getOverallScore(report);
    const radius = 42;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.min(Math.max(score / 5, 0), 1);
    const dashOffset = circumference * (1 - progress);
    const durationLabel = getSimulationDurationLabel(report);
    const proficiencyTone = getProficiencyTone(score);

    return (
        <div className="mbts_analysis_v2__hero">
            <div className="mbts_analysis_v2__page_head">
                <div className="mbts_analysis_v2__page_head__copy">
                    <h1 className="mbts_analysis_v2__page_title">
                        Simulation Insights & Growth Roadmap
                    </h1>
                    <p className="mbts_analysis_v2__page_subtitle">
                        Your cognitive audit from{' '}
                        <span>&quot;{report.scenario_title}&quot;</span> critical conversation.
                    </p>
                </div>

                <div className="mbts_analysis_v2__page_head__aside">
                    <div className="mbts_analysis_v2__view_switch">
                        <button
                            type="button"
                            className={`mbts_analysis_v2__view_switch__btn${viewMode === 'narrative' ? ' mbts_analysis_v2__view_switch__btn--active' : ''}`}
                            onClick={() => onViewModeChange('narrative')}
                        >
                            <Sparkles />
                            <span>Guided Story Flow</span>
                        </button>
                        <button
                            type="button"
                            className={`mbts_analysis_v2__view_switch__btn${viewMode === 'audit' ? ' mbts_analysis_v2__view_switch__btn--active' : ''}`}
                            onClick={() => onViewModeChange('audit')}
                        >
                            <LayoutGrid />
                            <span>Full 360 Audit</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className={spotlightClassName} {...spotlightHandlers}>
            <section className="mbts_analysis_v2__hero__card">
                <div className="mbts_analysis_v2__hero__glow" aria-hidden />

                <div className="mbts_analysis_v2__hero__layout">
                    <div className="mbts_analysis_v2__hero__score_panel">
                        <div className="mbts_analysis_v2__hero__gauge">
                            <svg viewBox="0 0 100 100" className="mbts_analysis_v2__hero__gauge__svg" aria-hidden>
                                <circle
                                    cx="50"
                                    cy="50"
                                    r={radius}
                                    className="mbts_analysis_v2__hero__gauge__track"
                                    fill="transparent"
                                    strokeWidth="8"
                                />
                                <circle
                                    cx="50"
                                    cy="50"
                                    r={radius}
                                    className="mbts_analysis_v2__hero__gauge__progress"
                                    fill="transparent"
                                    strokeWidth="8"
                                    strokeLinecap="round"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={dashOffset}
                                />
                            </svg>
                            <div className="mbts_analysis_v2__hero__gauge__value">
                                <span className="mbts_analysis_v2__hero__gauge__score">{score.toFixed(1)}</span>
                                <span className="mbts_analysis_v2__hero__gauge__scale">Out of 5.0</span>
                            </div>
                        </div>

                        <div className={`mbts_analysis_v2__hero__badge mbts_analysis_v2__hero__badge--${proficiencyTone}`}>
                            <Sprout />
                            <span>{getProficiencyCategory(score)} Profile</span>
                        </div>
                        <p className="mbts_analysis_v2__hero__score_caption">
                            Proficiency Index in High-Stakes Repair
                        </p>
                    </div>

                    <div className="mbts_analysis_v2__hero__narrative">
                        <div className="mbts_analysis_v2__hero__narrative__top">
                            <span className="mbts_analysis_v2__hero__takeaway_chip">Key Takeaway</span>
                            {durationLabel && (
                                <span className="mbts_analysis_v2__hero__duration">
                                    Simulation Time: {durationLabel}
                                </span>
                            )}
                        </div>

                        <h2 className="mbts_analysis_v2__hero__headline">
                            &quot;{getHeadline(report)}&quot;
                        </h2>

                        <div
                            className="mbts_analysis_v2__hero__body"
                            dangerouslySetInnerHTML={{ __html: getNarrativeBodyHtml(report) }}
                        />

                        <div className="mbts_analysis_v2__hero__story_cards">
                            <button
                                type="button"
                                className="mbts_analysis_v2__hero__story_card"
                                onClick={() => {
                                    onViewModeChange('narrative');
                                    setTimeout(() => scrollToSectionId(SECTION_IDS.mastery), 50);
                                }}
                            >
                                <span className="mbts_analysis_v2__hero__story_card__label">
                                    <Bolt />
                                    Current Baseline
                                </span>
                                <span className="mbts_analysis_v2__hero__story_card__value">
                                    {report.cognitive_profile}
                                </span>
                            </button>

                            <button
                                type="button"
                                className="mbts_analysis_v2__hero__story_card"
                                onClick={() => {
                                    onViewModeChange('narrative');
                                    setTimeout(() => scrollToSectionId(SECTION_IDS.lab), 50);
                                }}
                            >
                                <span className="mbts_analysis_v2__hero__story_card__label">
                                    <TriangleAlert />
                                    Friction Spiral
                                </span>
                                <span className="mbts_analysis_v2__hero__story_card__value">
                                    {getFrictionLabel(report)}
                                </span>
                            </button>

                            <button
                                type="button"
                                className="mbts_analysis_v2__hero__story_card"
                                onClick={() => {
                                    onViewModeChange('narrative');
                                    setTimeout(() => scrollToSectionId(SECTION_IDS.habits), 50);
                                }}
                            >
                                <span className="mbts_analysis_v2__hero__story_card__label">
                                    <Crosshair />
                                    Quick Win
                                </span>
                                <span className="mbts_analysis_v2__hero__story_card__value">
                                    {getQuickWinLabel(report)}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            </div>
        </div>
    );
};
